import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/api";
import { useLang } from "@/i18n";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Upload } from "lucide-react";
import { ExerciseMedia, isExternalMediaLink } from "@/pages/client/ClientHome";

const EMPTY = { name: "", muscle_group: "", equipment: "", description: "", video_url: "" };

export default function Exercises() {
  const { t } = useLang();
  const [exercises, setExercises] = useState([]);
  const [filter, setFilter] = useState("");
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [mediaFile, setMediaFile] = useState(null);

  const nav = [
    { to: "/coach", key: "dashboard", label: t("nav.dashboard") },
    { to: "/coach/clients", key: "clients", label: t("nav.clients") },
    { to: "/coach/exercises", key: "exercises", label: t("nav.exercises"), end: true },
    { to: "/coach/programs", key: "programs", label: t("nav.programs") },
    { to: "/coach/nutrition", key: "nutrition", label: t("nav.nutrition") },
    { to: "/coach/supplements", key: "supplements", label: t("nav.supplements") },
    { to: "/coach/stretching", key: "stretching", label: t("nav.stretching") },
    { to: "/coach/shop", key: "shop", label: t("nav.shop") },
    { to: "/coach/testimonials", key: "testimonials", label: t("nav.testimonials") },
    { to: "/coach/education", key: "education", label: t("nav.education") },
  ];

  const load = () => api.get("/exercises").then((r) => setExercises(r.data));
  useEffect(() => { load(); }, []);

  const groups = useMemo(() => [...new Set(exercises.map((e) => e.muscle_group).filter(Boolean))], [exercises]);
  const filtered = filter ? exercises.filter((e) => e.muscle_group === filter) : exercises;

  const openNew = () => { setForm(EMPTY); setMediaFile(null); setModal("new"); };
  const openEdit = (ex) => { setForm({ name: ex.name, muscle_group: ex.muscle_group, equipment: ex.equipment, description: ex.description, video_url: ex.video_url }); setMediaFile(null); setModal(ex); };

  const save = async () => {
    try {
      let exId;
      if (modal === "new") {
        const r = await api.post("/exercises", form);
        exId = r.data._id;
      } else {
        await api.put(`/exercises/${modal._id}`, form);
        exId = modal._id;
      }
      if (mediaFile) {
        const fd = new FormData();
        fd.append("file", mediaFile);
        await api.post(`/exercises/${exId}/media`, fd);
      }
      toast.success(t("common.save") + " ✓");
      setModal(null);
      setMediaFile(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    await api.delete(`/exercises/${id}`);
    load();
  };

  return (
    <Layout nav={nav}>
      <div data-testid="exercises-page">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h1 className="text-3xl sm:text-4xl font-black uppercase tracking-tight">{t("ex.title")}</h1>
          <button data-testid="new-exercise-button" onClick={openNew} className="kc-btn-primary !py-2.5 flex items-center gap-2">
            <Plus className="w-4 h-4" /> {t("ex.new")}
          </button>
        </div>

        <div className="flex gap-2 mb-6 flex-wrap">
          <button data-testid="filter-all-button" onClick={() => setFilter("")}
            className={`kc-badge cursor-pointer ${!filter ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>
            {t("ex.filterAll")}
          </button>
          {groups.map((g) => (
            <button key={g} data-testid={`filter-${g}`} onClick={() => setFilter(g)}
              className={`kc-badge cursor-pointer ${filter === g ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>
              {g}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <p className="text-muted-foreground" data-testid="exercises-empty">{t("ex.empty")}</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((ex) => (
              <div key={ex._id} className="kc-card kc-card-hover p-5" data-testid="exercise-library-card">
                <div className="flex justify-between items-start gap-2">
                  <h3 className="font-bold">{ex.name}</h3>
                  <div className="flex gap-1">
                    <button data-testid={`edit-exercise-${ex._id}`} onClick={() => openEdit(ex)} className="kc-btn-ghost !p-1.5"><Pencil className="w-4 h-4" /></button>
                    <button data-testid={`delete-exercise-${ex._id}`} onClick={() => remove(ex._id)} className="kc-btn-ghost !p-1.5 text-destructive"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                <div className="flex gap-2 mt-2 flex-wrap">
                  {ex.muscle_group && <span className="kc-badge bg-accent/15 text-accent">{ex.muscle_group}</span>}
                  {ex.equipment && <span className="kc-badge bg-secondary text-muted-foreground">{ex.equipment}</span>}
                </div>
                {ex.description && <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{ex.description}</p>}
                {ex.video_url && (isExternalMediaLink(ex.video_url) ? (
                  <a data-testid={`video-link-${ex._id}`} href={ex.video_url} target="_blank" rel="noreferrer"
                    className="text-xs text-primary hover:underline mt-2 inline-block break-all">{ex.video_url}</a>
                ) : (
                  <ExerciseMedia url={ex.video_url} className="mt-3 rounded-xl w-full h-40 object-contain bg-secondary/40" />
                ))}
              </div>
            ))}
          </div>
        )}

        {modal && (
          <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="exercise-modal">
            <div className="kc-card bg-popover p-6 w-full max-w-md space-y-3">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold">{modal === "new" ? t("ex.new") : t("common.edit")}</h3>
                <button data-testid="exercise-modal-close" onClick={() => setModal(null)} className="kc-btn-ghost !p-1.5"><X className="w-5 h-5" /></button>
              </div>
              {[["name", "ex.name"], ["muscle_group", "ex.muscle"], ["equipment", "ex.equipment"], ["video_url", "ex.video"]].map(([k, label]) => (
                <div key={k}>
                  <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t(label)}</label>
                  <input data-testid={`exercise-${k}-input`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="kc-input mt-1" />
                </div>
              ))}
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("ex.uploadMedia")}</label>
                <label className="mt-1 flex items-center gap-2 kc-input cursor-pointer text-sm text-muted-foreground" data-testid="exercise-media-upload">
                  <Upload className="w-4 h-4 text-primary shrink-0" />
                  <span className="truncate">{mediaFile ? mediaFile.name : "GIF / MP4 / WebM"}</span>
                  <input type="file" accept="image/gif,video/mp4,video/webm,video/quicktime" className="hidden"
                    data-testid="exercise-media-file-input" onChange={(e) => setMediaFile(e.target.files?.[0] || null)} />
                </label>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("ex.desc")}</label>
                <textarea data-testid="exercise-description-input" value={form.description} rows={3}
                  onChange={(e) => setForm({ ...form, description: e.target.value })} className="kc-input mt-1" />
              </div>
              <button data-testid="exercise-save-button" onClick={save} disabled={!form.name.trim()} className="kc-btn-primary w-full !py-2.5">
                {t("common.save")}
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
